/* eslint-disable react/jsx-no-target-blank */
import { ArticleList } from "app/articles/components/ArticleList"
import { BrandSocialLinkList } from "app/brands/components/BrandSocialLinkList"
import { WhereToBuy } from "app/brands/components/WhereToBuy"
import { categoryForestToTree } from "app/categories/categoryForestToTree"
import { CategoryPageLinkTree } from "app/categories/components/CategoryPageLinkTree"
import { RouteMgr } from "app/common/routes"
import { optionalRender } from "app/common/ui/optionalRender"
import { variantsToSrcSet } from "app/common/utilities/html"
import { BrandVM, hasSomeSocialLinks } from "app/models"
import { TagList } from "app/tags/components/TagList"
import { Link } from "blitz"
import { LocaleD } from "db"
import { array, string } from "fp-ts"
import { pipe } from "fp-ts/function"
import useTranslation from "next-translate/useTranslation"
import React, { FC } from "react"
import { BiGift, BiRepeat } from "react-icons/bi"
import { IoMdCalendar } from "react-icons/io"
import { MdCardGiftcard, MdWeb } from "react-icons/md"
import { RiParentLine } from "react-icons/ri"
import { TiFlowChildren } from "react-icons/ti"
import { tw } from "twind"

const BrandUrl: FC<{ url: string }> = ({ url }) => (
  <a className={tw(`blogLink`)} href={url} rel="noopener noreferrer" target="_blank">
    {pipe(url, string.replace("http://", ""))}
  </a>
)

interface BrandMetadataPanelProps {
  brand: BrandVM
  locale: LocaleD
}

export const metadataH2 = tw`text-base font-semibold mb-2`

export const BrandMetadataPanel: FC<BrandMetadataPanelProps> = ({ brand, locale }) => {
  const { t } = useTranslation("brands")

  return (
    <div className={tw(`flex(& col) gap-6`)}>
      <div
        className={tw(
          `flex(& col) gap-4`,
          `md:shadow`,
          `md:border(1 gray-200)`,
          `md:mt-2`,
          `md:(px-6 py-4)`
        )}
      >
        {pipe(
          brand.logo,
          optionalRender((logo) => (
            <img
              alt={logo.alternateText}
              className={tw(`w(28 sm:36) self-center`)}
              height={logo.height}
              key={logo.url}
              sizes={"400px"}
              src={logo.url}
              srcSet={variantsToSrcSet(logo.variants)}
              width={logo.width}
            />
          ))
        )}

        {pipe(
          brand.url,
          optionalRender((url) => (
            <div className={tw(`flex gap-2`, `text-sm`)}>
              <MdWeb className={tw(`text-xl`)} />
              <div>
                <div className={tw(`font-semibold`)}>{t("website")}</div>
                <BrandUrl key={url} url={url} />
              </div>
            </div>
          ))
        )}

        {pipe(
          brand.establishedAt,
          optionalRender((year) => (
            <div className={tw(`flex gap-2`, `text-sm`)}>
              <IoMdCalendar className={tw(`text-xl`)} />
              <div>
                <div className={tw(`font-semibold`)}>{t("established")}</div>
                <div>{year.toString()}</div>
              </div>
            </div>
          ))
        )}

        {brand.hasGiftcards && (
          <div className={tw(`flex gap-2`, `text-sm`)}>
            <MdCardGiftcard className={tw(`text-xl`)} />
            <span className={tw(`font-semibold`)}>{t("has-giftcards")}</span>
          </div>
        )}

        {brand.hasGiftboxes && (
          <div className={tw(`flex gap-2`, `text-sm`)}>
            <BiGift className={tw(`text-xl`)} />
            <span className={tw(`font-semibold`)}>{t("has-giftsets")}</span>
          </div>
        )}

        {brand.hasSubscriptions && (
          <div className={tw(`flex gap-2`, `text-sm`)}>
            <BiRepeat className={tw(`text-xl`)} />
            <span className={tw(`font-semibold`)}>{t("has-subscriptions")}</span>
          </div>
        )}

        {pipe(
          brand.parent,
          optionalRender((parent) => (
            <div className={tw(`flex gap-2`, `text-sm`)}>
              <RiParentLine className={tw(`text-xl`)} />
              <div>
                <span className={tw(`font-semibold`)}>{t("parent-brand")}</span>
                <Link href={RouteMgr.BrandPage({ slug: parent.slug })}>
                  <a className={tw("blogLink", "text-sm")}>{parent.name}</a>
                </Link>
              </div>
            </div>
          ))
        )}

        {pipe(
          brand.subbrands,
          array.map((subbrand) => (
            <li key={subbrand.slug}>
              <Link href={RouteMgr.BrandPage({ slug: subbrand.slug })}>
                <a className={tw("blogLink", "text-sm")}>{subbrand.name}</a>
              </Link>
            </li>
          )),
          (as) =>
            array.isNonEmpty(as) && (
              <div className={tw(`flex gap-2`, `text-sm`)}>
                <TiFlowChildren className={tw(`text-xl`)} />

                <div>
                  <div className={tw(`font-semibold`)}>{t("Subbrands")}</div>
                  <ul>{as}</ul>
                </div>
              </div>
            )
        )}

        <TagList orientation="vertical" tags={brand.tags} />

        <WhereToBuy brand={brand} />

        {hasSomeSocialLinks(brand) && <BrandSocialLinkList socialLinkable={brand} />}

        <CategoryPageLinkTree categories={categoryForestToTree(brand.categories)} showTitle />
      </div>

      <div className={tw(`grid md:max-w-sm`)}>
        <ArticleList articles={brand.articles} locale={locale} />
      </div>
    </div>
  )
}
